import { useCallback, useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { DocumentEditor } from "@/features/workspace/components/DocumentEditor"
import { isNodeChanged, type ChangedRange } from "@/features/workspace/lib/diffRangeHighlight"
import { putSection } from "./api"
import { noteEditRefusal, refreshEditAllowance } from "../order-sim/editLimit"
import { ConflictDialog, type ConflictChoice } from "./ConflictDialog"
import { registerEdit, useRecoveredEdits, type FlushOutcome } from "./edits"
import { HistoryDrawer } from "./HistoryDrawer"
import { changedRanges } from "./lib/changedRanges"
import { DISCUSSION_GROUP, sectionLabel, stripLeadingHeading, type PlannedBlock, type SectionHeading, type SectionTitles } from "./sections"
import { useSimStore } from "./store"
import type { SectionDraft } from "./contract"

export const AUTOSAVE_MS = 2000

type SaveState = "saved" | "dirty" | "saving" | "error" | "conflict"
const STATE_TEXT: Record<SaveState, string> = {
  saved: "Saved", dirty: "Unsaved changes", saving: "Saving…", error: "Couldn't save. Retrying on your next edit.", conflict: "Needs your decision",
}

interface Conflict { mine: string; agent: string; agentVersion: number }

/** The lines the agent changed in its latest save, drawn with the shared changed-range test. */
function ChangedLines({ markdown, ranges, onDismiss }: { markdown: string; ranges: ChangedRange[]; onDismiss: () => void }) {
  const lines = markdown.split("\n")
  const hits = lines
    .map((text, i) => ({ text, line: i + 1 }))
    .filter(({ text, line }) => text.trim() && isNodeChanged({ start: { line }, end: { line } }, ranges))
    .slice(0, 12)
  if (!hits.length) return null
  return (
    <div className="osim-changed" role="status" aria-label="Changed by the agent">
      <div className="osim-changed-head">
        <span className="lab">Changed by the agent</span>
        <button type="button" className="scb" onClick={onDismiss}>Dismiss</button>
      </div>
      {hits.map((h) => <p key={h.line} className="osim-changed-line"><span className="mono">{h.line}</span>{h.text}</p>)}
    </div>
  )
}

/** Non-editable paragraph numbers beside the editor. The editor's own text stays unnumbered. */
function Gutter({ blocks, watch }: { blocks: PlannedBlock[]; watch: React.RefObject<HTMLDivElement | null> }) {
  const labels = blocks.flatMap((b) => b.labels.map((label, t) => ({ label, block: b, target: t }))).filter((x) => x.label)
  const [tops, setTops] = useState<number[]>([])
  const key = blocks.map((b) => b.labels.join(",")).join("|")
  useEffect(() => {
    const host = watch.current
    if (!host) return
    let frame = 0
    const measure = () => {
      const pm = host.querySelector(".ProseMirror")
      const hostTop = host.getBoundingClientRect().top
      const next: number[] = []
      blocks.forEach((b, bi) => {
        const el = pm?.children[bi] as HTMLElement | undefined
        b.labels.forEach((label, t) => {
          if (!label) return
          const target = b.kind === "list" ? (el?.querySelectorAll(":scope > li")[t] as HTMLElement | undefined) : el
          next.push(target ? Math.max(0, target.getBoundingClientRect().top - hostTop) : 0)
        })
      })
      setTops((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next))
    }
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure) }
    schedule()
    const mo = typeof MutationObserver === "undefined" ? null : new MutationObserver(schedule)
    mo?.observe(host, { childList: true, subtree: true, characterData: true })
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule)
    ro?.observe(host)
    return () => { cancelAnimationFrame(frame); mo?.disconnect(); ro?.disconnect() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, watch])
  if (!labels.length) return null
  return (
    <div className="osim-gutter" aria-hidden="true" data-testid="osim-gutter">
      {labels.map((x, i) => <span key={i} className="osim-pn" style={{ top: tops[i] ?? 0 }}>{x.label}</span>)}
    </div>
  )
}

interface Props {
  threadId: string
  sectionId: string
  section: SectionDraft | undefined
  /** Live text while the agent writes this section. */
  streaming: string | null
  /** Canonical heading drawn above the body. */
  heading?: SectionHeading
  /** Draw the "5. DISCUSSION AND FINDINGS" group heading above this section's own. */
  groupHeading?: boolean
  /** Section ids on the page, for numbering the label. */
  ids?: readonly string[]
  /** Allegation titles for the "Issue n: <title>" label. */
  titles?: SectionTitles
  /** Render-time numbering for this section's blocks (the page-wide counter), drawn in the gutter. */
  numbers?: PlannedBlock[]
}

/**
 * One draft section: editable markdown that autosaves 2s after the last edit.
 *
 * No edit is dropped silently:
 * - every save sends the version the user's text was based on, so a stale save meets a 409 and the conflict dialog;
 * - an agent save that arrives while the user has unsaved text opens the dialog instead of replacing the editor;
 * - leaving the page saves the edit, and an edit that cannot be saved is stashed and offered back on the next mount.
 */
export function SectionEditor({ threadId, sectionId, section, streaming, heading, groupHeading, ids, titles, numbers }: Props) {
  const key = `${threadId}:${sectionId}`
  const label = sectionLabel(sectionId, ids, titles)
  const edHost = useRef<HTMLDivElement | null>(null)
  const [content, setContent] = useState(stripLeadingHeading(section?.markdown ?? ""))
  const [editorKey, setEditorKey] = useState(0)
  const [state, setState] = useState<SaveState>("saved")
  const [conflict, setConflict] = useState<Conflict | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [ranges, setRanges] = useState<ChangedRange[]>([])
  const [recovered, setRecovered] = useState(() => useRecoveredEdits.getState().peek(key) ?? null)
  const [tick, setTick] = useState(0)

  const mine = useRef<string | null>(null) // unsaved text the editor has handed over
  const typing = useRef(false) // the user typed since the last sync (text may still be inside the editor's debounce)
  const editBase = useRef(section?.version ?? 0) // the server version the unsaved text derives from
  const serverMd = useRef(section?.markdown ?? "")
  const version = useRef(section?.version ?? 0)
  const timer = useRef(0)
  const inflight = useRef<Promise<FlushOutcome> | null>(null)
  const conflictRef = useRef<Conflict | null>(null)
  const editorFlush = useRef<(() => void) | null>(null)
  const flushRef = useRef<() => Promise<FlushOutcome>>(() => Promise.resolve("idle"))

  const openConflict = useCallback((c: Conflict) => {
    window.clearTimeout(timer.current)
    conflictRef.current = c
    setConflict(c)
    setDialogOpen(true)
    setState("conflict")
  }, [])

  const commit = useCallback((markdown: string, newVersion: number, author: SectionDraft["author"]) => {
    serverMd.current = markdown
    version.current = newVersion
    useSimStore.getState().setSection(threadId, sectionId, { markdown, version: newVersion, author })
  }, [threadId, sectionId])

  const schedule = useCallback(() => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => { void flushRef.current() }, AUTOSAVE_MS)
  }, [])

  const flush = useCallback((): Promise<FlushOutcome> => {
    window.clearTimeout(timer.current)
    editorFlush.current?.() // text still inside the editor's own debounce reaches `mine` first
    if (conflictRef.current) return Promise.resolve("conflict")
    if (inflight.current) return inflight.current.then(() => flushRef.current())
    const md = mine.current
    if (md === null) return Promise.resolve("idle")
    const base = editBase.current
    setState("saving")
    const run = (async (): Promise<FlushOutcome> => {
      try {
        const r = await putSection(threadId, sectionId, md, base)
        editorFlush.current?.()
        if (r.ok) {
          refreshEditAllowance(threadId)
          commit(md, r.version, "user")
          editBase.current = r.version
          if (mine.current === md) {
            mine.current = null
            typing.current = false
            setState("saved")
          } else {
            // Edited while the save was in flight: save again after this one.
            setState("dirty")
            schedule()
          }
          return "saved"
        }
        openConflict({ mine: mine.current ?? md, agent: r.currentMarkdown, agentVersion: r.currentVersion })
        return "conflict"
      } catch (err) {
        noteEditRefusal(threadId, err)
        setState("error")
        return "error"
      } finally {
        inflight.current = null
        setTick((t) => t + 1)
      }
    })()
    inflight.current = run
    return run
  }, [threadId, sectionId, commit, openConflict, schedule])
  flushRef.current = flush

  const onUserEdit = useCallback(() => {
    typing.current = true
    setState((s) => (s === "saved" ? "dirty" : s))
  }, [])

  const onEdit = useCallback((markdown: string) => {
    if (markdown === stripLeadingHeading(serverMd.current) && !inflight.current) {
      // Back to what the server has: nothing to save.
      mine.current = null
      typing.current = false
      window.clearTimeout(timer.current)
      if (!conflictRef.current) setState("saved")
      return
    }
    if (mine.current === null) editBase.current = version.current
    mine.current = markdown
    setState((s) => (s === "conflict" ? s : "dirty"))
    schedule()
  }, [schedule])

  // Changes that did not come from this editor: agent saves, restores, hydrate.
  useEffect(() => {
    if (!section || inflight.current) return
    if (section.version === version.current && section.markdown === serverMd.current) return
    editorFlush.current?.() // the user's last keystrokes count before deciding
    if (mine.current !== null) {
      if (section.version <= version.current) return
      if (conflictRef.current?.agentVersion === section.version) return
      openConflict({ mine: mine.current, agent: section.markdown, agentVersion: section.version })
      return
    }
    typing.current = false
    const prev = serverMd.current
    const hadVersion = version.current > 0 // the first load of a section is not a change
    serverMd.current = section.markdown
    version.current = section.version
    editBase.current = section.version
    setContent(stripLeadingHeading(section.markdown))
    setEditorKey((k) => k + 1)
    setRanges(hadVersion && section.author === "agent" ? changedRanges(prev, section.markdown) : [])
  }, [section, tick, openConflict])

  // Register for the page-level flush (Download DOCX) and the beforeunload warning.
  useEffect(() => registerEdit(key, {
    threadId,
    isDirty: () => mine.current !== null || typing.current || conflictRef.current !== null,
    flush: () => flushRef.current(),
    beacon: () => {
      editorFlush.current?.()
      const md = conflictRef.current ? null : mine.current
      if (md !== null) void putSection(threadId, sectionId, md, editBase.current, { keepalive: true }).catch(() => undefined)
    },
  }), [key, threadId, sectionId])

  // Leaving (unmount, thread switch) saves a pending edit; if that fails the text is stashed for the next visit.
  useEffect(() => () => {
    window.clearTimeout(timer.current)
    void flushRef.current().then((outcome) => {
      if (outcome !== "conflict" && outcome !== "error") return
      const text = mine.current ?? conflictRef.current?.mine
      if (text !== undefined && text !== null) useRecoveredEdits.getState().stash(key, { markdown: text, baseVersion: editBase.current })
    })
  }, [key])

  const restoreRecovered = () => {
    if (!recovered) return
    useRecoveredEdits.getState().drop(key)
    mine.current = recovered.markdown
    editBase.current = recovered.baseVersion
    typing.current = true
    setContent(recovered.markdown)
    setEditorKey((k) => k + 1)
    setRecovered(null)
    setState("dirty")
    schedule()
  }
  const discardRecovered = () => {
    useRecoveredEdits.getState().drop(key)
    setRecovered(null)
  }

  const resolve = async (choice: ConflictChoice) => {
    const c = conflictRef.current
    if (!c) return
    conflictRef.current = null
    setConflict(null)
    setDialogOpen(false)
    if (choice.kind === "agent") {
      mine.current = null
      typing.current = false
      commit(c.agent, c.agentVersion, "agent")
      editBase.current = c.agentVersion
      setContent(stripLeadingHeading(c.agent))
      setEditorKey((k) => k + 1)
      setRanges([])
      setState("saved")
      return
    }
    const md = choice.kind === "merged" ? choice.markdown : (mine.current ?? c.mine)
    // The agent's text is now the baseline; the save builds on it.
    serverMd.current = c.agent
    version.current = c.agentVersion
    editBase.current = c.agentVersion
    mine.current = md
    if (choice.kind === "merged") { setContent(stripLeadingHeading(md)); setEditorKey((k) => k + 1) }
    await flush()
  }

  const reopenConflict = () => {
    if (conflictRef.current && mine.current !== null) conflictRef.current = { ...conflictRef.current, mine: mine.current }
    setConflict(conflictRef.current)
    setDialogOpen(true)
  }

  const isStreaming = streaming !== null
  // An agent save with no text yet is not a draft: show the placeholder line, not an empty editor.
  const empty = !section || (section.author === "agent" && !section.markdown.trim() && mine.current === null && !recovered)
  const showState = state !== "saved" || isStreaming
  return (
    <section className={`osim-sec${ranges.length ? " osim-flash" : ""}`} aria-label={label} data-section={sectionId}>
      {groupHeading ? <h2 className="osim-h">{heading?.group ?? DISCUSSION_GROUP}</h2> : null}
      {(heading ? heading.text : label) ? (
        <h2 className="osim-h" data-level={heading?.level ?? 1} data-center={heading?.center ? "1" : undefined}>{heading ? heading.text : label}</h2>
      ) : null}
      <span className={`osim-margin${showState ? " is-live" : ""}`}>
        <span className={`osim-state osim-state--${state}`} role="status">{isStreaming ? "Drafting…" : STATE_TEXT[state]}</span>
        {section ? <span aria-hidden="true"> · v{section.version}</span> : null}
        {conflict && !dialogOpen ? <button type="button" className="scb" onClick={reopenConflict}>Resolve</button> : null}
        <span aria-hidden="true"> · </span>
        <button type="button" className="scb" disabled={!section} onClick={() => setHistoryOpen(true)}>History</button>
      </span>
      {recovered ? (
        <div className="osim-changed" role="alert" aria-label="Unsaved edit recovered">
          <div className="osim-changed-head">
            <span className="lab">Unsaved edit recovered</span>
            <span>
              <button type="button" className="scb" onClick={restoreRecovered}>Restore my edit</button>
              <button type="button" className="scb" onClick={discardRecovered}>Discard</button>
            </span>
          </div>
          <p className="osim-changed-line">Your last edit to this section could not be saved. {recovered.markdown.slice(0, 120)}{recovered.markdown.length > 120 ? "…" : ""}</p>
        </div>
      ) : null}
      {isStreaming ? (
        <div className="osim-stream osim-md" aria-live="polite"><ReactMarkdown remarkPlugins={[remarkGfm]}>{streaming}</ReactMarkdown></div>
      ) : !empty ? (
        <>
          {ranges.length ? <ChangedLines markdown={section?.markdown ?? ""} ranges={ranges} onDismiss={() => setRanges([])} /> : null}
          <div className="osim-ed" ref={edHost}>
            {numbers?.length ? <Gutter blocks={numbers} watch={edHost} /> : null}
            <DocumentEditor
              key={editorKey}
              content={content}
              onUpdate={onEdit}
              onUserEdit={onUserEdit}
              flushRef={editorFlush}
              className="min-h-0 p-0 mx-0 max-w-none shadow-none bg-transparent"
            />
          </div>
        </>
      ) : (
        <p className="osim-undrafted">— not drafted yet —</p>
      )}
      {conflict && dialogOpen ? (
        <ConflictDialog sectionLabel={label} mine={conflict.mine} agent={conflict.agent} onResolve={(c) => void resolve(c)} onClose={() => setDialogOpen(false)} />
      ) : null}
      {historyOpen ? <HistoryDrawer threadId={threadId} sectionId={sectionId} label={label} onClose={() => setHistoryOpen(false)} /> : null}
    </section>
  )
}
